import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../../services/api';
import { useSignup } from '../../components/redux';
import { useOnboarding } from '../../components/redux/hooks/useOnboarding';
import { createSession, fetchSessionDetails } from '../../components/redux/slices/onboardingSlice';
import { usePostHog, POSTHOG_EVENTS } from '../../components/analytics/PostHogProvider';
import JotformEmbed from '../../components/JotformEmbed';
import LoginModal from './modals/LoginModal';


const SignupPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    miraFTE,
    frankFTE,
    organizationName,
    selectedOption,
    orgSearchResults,
    isSearchingOrgs,
    showOrgSuggestions,
    selectedOrgId,
    updateMiraFTE,
    updateFrankFTE,
    updateOrganizationName,
    updateSelectedOption,
    updateSelectedOrgId,
    updateOrgSearchResults,
    updateIsSearchingOrgs,
    updateShowOrgSuggestions,
    updateEmailError,
    updateCartCapabilities,
  } = useSignup();
  
  const {
    sessionId,
    isCreatingSession,
    sessionError,
    sessionCreated,
    sessionData,
    isFetchingSession,
    fetchSessionError,
    isAuthenticated,
    setOrganizationIdState,
  } = useOnboarding();
  const searchDebounceTimer = useRef(null);
  const sessionCreationRef = useRef(false);
  const { captureEvent, identifyUser } = usePostHog();

  // Contract signing state
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingContractShow, setPendingContractShow] = useState(false);

  // Loading states
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // ROI Calculator State - Capabilities (matching ROICalculator.jsx)
  const [selectedCapabilities, setSelectedCapabilities] = useState({
    appointments: true,
    medications: true,
    medicalIssues: true,
    billingInsurance: true,
    medicalRecords: true,
    generalInquiries: false,
    providerCalls: false,
    vendorCalls: false
  });

  // Call volumes per capability (daily calls)
  const [callVolumes, setCallVolumes] = useState({
    appointments: 50,
    medications: 50,
    medicalIssues: 50,
    billingInsurance: 50,
    medicalRecords: 50,
    generalInquiries: 50,
    providerCalls: 50,
    vendorCalls: 50
  });

  // Detailed capabilities for enhanced ROI calculations
  const [detailedCapabilities, setDetailedCapabilities] = useState({
    appointments: {
      provideInfo: false,
      reschedule: false,
      simpleReschedule: false,
      complexReschedule: false,
      cancel: false
    },
    medications: {
      provideInfo: false,
      collectRefillInfo: false,
      sendNote: false,
      callPharmacy: false,
      updateEHR: false
    },
    medicalIssues: {
      collectInfo: false,
      lookupNotes: false,
      createNote: false,
      sendTeamsMessage: false,
      sendEHRMessage: false,
      followGuidelines: false
    },
    billingInsurance: {
      provideInfo: false,
      updateInsurance: false,
      payBills: false,
      getEstimates: false
    },
    medicalRecords: {
      submitRequest: false,
      getStatus: false,
      signUpPortal: false
    },
    generalInquiries: {
      hoursDirections: false,
      urgentNotifications: false,
      otherInquiries: false
    },
    providerCalls: {
      doctorOffice: false,
      emergencyRooms: false,
      otherFacilities: false
    },
    vendorCalls: {
      approvedVendors: false,
      unknownVendors: false,
      otherCallers: false
    }
  });

  // Mira tasks (clinical)
  const [miraTasks, setMiraTasks] = useState({
    medicalIssues: true,
    medicalRecords: true,
    clinicalDocumentation: true
  });

  // Frank tasks (admin)
  const [frankTasks, setFrankTasks] = useState({
    appointments: true,
    medications: true,
    billingInsurance: false,
    vendorCalls: false
  });

  // Manual FTE overrides (null means use calculated value)
  const [manualMiraFTEs, setManualMiraFTEs] = useState(null);
  const [manualFrankFTEs, setManualFrankFTEs] = useState(null);

  // Capabilities configuration
  const capabilities = [
    {
      id: 'appointments',
      icon: '📅',
      title: 'Appointments',
      description: 'Schedule, reschedule, and manage appointments',
      assignedTo: 'frank'
    },
    {
      id: 'medications',
      icon: '💊',
      title: 'Medications / Pharmacy',
      description: 'Handle prescription refills and pharmacy questions',
      assignedTo: 'frank'
    },
    {
      id: 'medicalIssues',
      icon: '🏥',
      title: 'Medical Issues (Triage Assist)',
      description: 'Collect detailed information for medical triage',
      assignedTo: 'mira'
    },
    {
      id: 'billingInsurance',
      icon: '💳',
      title: 'Billing / Insurance',
      description: 'Handle billing questions and insurance updates',
      assignedTo: 'frank'
    },
    {
      id: 'medicalRecords',
      icon: '📋',
      title: 'Medical Records',
      description: 'Manage medical records requests',
      assignedTo: 'mira'
    },
    {
      id: 'generalInquiries',
      icon: 'ℹ️',
      title: 'General Inquiries',
      description: 'Answer general clinic questions',
      assignedTo: 'both'
    },
    {
      id: 'providerCalls',
      icon: '👥',
      title: 'Provider Calls',
      description: 'Handle calls from doctor offices and medical facilities',
      assignedTo: 'both'
    },
    {
      id: 'vendorCalls',
      icon: '📦',
      title: 'Vendor Calls',
      description: 'Manage calls from approved and unknown vendors',
      assignedTo: 'frank'
    }
  ];

  // Enhanced ROI calculations across 3 domains: Revenue, Margins/Cost Savings, Patient Access
  // (Replicated from ROICalculator.jsx)
  const calculations = useMemo(() => {
    const avgCallDuration = 5; // minutes
    const avgStaffHourlyRate = 25; // dollars (including benefits)
    const ankrSavingsRate = 0.70; // 70% time savings
    const daysPerMonth = 22; // working days

    // Count selected detailed capabilities for bonus multiplier
    let selectedDetailsCount = 0;
    Object.keys(detailedCapabilities).forEach(capKey => {
      Object.keys(detailedCapabilities[capKey]).forEach(detailKey => {
        if (detailedCapabilities[capKey][detailKey]) {
          selectedDetailsCount++;
        }
      });
    });
    // Apply 6-10% improvement per selected capability detail
    const improvementPerDetail = 0.06 + (Math.random() * 0.04); // 6-10%
    const capabilityMultiplier = Math.pow(1 + improvementPerDetail, selectedDetailsCount);

    let totalCalls = 0;
    let monthlySavingsMinutes = 0;

    Object.keys(selectedCapabilities).forEach(key => {
      if (selectedCapabilities[key]) {
        const dailyCalls = callVolumes[key] || 0;
        totalCalls += dailyCalls;
        monthlySavingsMinutes += dailyCalls * avgCallDuration * ankrSavingsRate * daysPerMonth;
      }
    });

    const monthlySavingsHours = (monthlySavingsMinutes / 60) * capabilityMultiplier;
    const monthlySavingsDollars = monthlySavingsHours * avgStaffHourlyRate;
    const annualSavingsDollars = monthlySavingsDollars * 12;

    // Calculate Mira and Frank costs
    const fteCostPerMonth = 1820; // $10.5/hour x 40 hours/week x ~4.33 weeks
    const fteCostPerYear = 21840;

    // Mira FTEs: 1 FTE per 100 calls/day (use manual override if available)
    const calculatedMiraFTEs = Math.ceil(totalCalls / 100);
    const miraFTEs = manualMiraFTEs !== null ? manualMiraFTEs : calculatedMiraFTEs;

    // Frank FTEs: For appointments, medical issues, and billing/insurance (100 calls each)
    const frankCallsPerDay = (selectedCapabilities.appointments ? (callVolumes.appointments || 0) : 0) +
                              (selectedCapabilities.medicalIssues ? (callVolumes.medicalIssues || 0) : 0) +
                              (selectedCapabilities.billingInsurance ? (callVolumes.billingInsurance || 0) : 0);
    const calculatedFrankFTEs = Math.ceil(frankCallsPerDay / 100);
    const frankFTEs = manualFrankFTEs !== null ? manualFrankFTEs : calculatedFrankFTEs;

    const totalFTEs = miraFTEs + frankFTEs;
    const totalMonthlyCost = totalFTEs * fteCostPerMonth;
    const totalAnnualCost = totalFTEs * fteCostPerYear;

    // Revenue Impact Calculations (with capability multiplier)
    const appointmentCalls = selectedCapabilities.appointments ? (callVolumes.appointments || 0) : 0;
    const appointmentConversionRate = 0.05; // 5% of appointment calls convert to scheduled appointments
    const appointmentShowRate = 0.60; // 60% show up
    const avgRevenuePerVisit = 150; // Average revenue per patient visit
    const newAppointmentsPerMonth = appointmentCalls * daysPerMonth * appointmentConversionRate * appointmentShowRate * capabilityMultiplier;
    const monthlyRevenueIncrease = newAppointmentsPerMonth * avgRevenuePerVisit;
    const annualRevenueIncrease = monthlyRevenueIncrease * 12;

    // Net Impact (after Ankr costs)
    const netAnnualSavings = annualSavingsDollars + annualRevenueIncrease - totalAnnualCost;
    
    // ROI Ratio calculation (total savings / total cost)
    const roiRatio = totalAnnualCost > 0 ? ((annualSavingsDollars + annualRevenueIncrease) / totalAnnualCost) : 0;

    // Patient Access Improvements (with capability multiplier)
    const avgWaitTimeReduction = Math.min(75 * capabilityMultiplier, 95); // 75% reduction in phone wait time, capped at 95%
    const callsAnsweredRate = Math.min(0.95 * capabilityMultiplier, 0.99); // 95% of calls answered (vs typical 60-70%), capped at 99%
    const missedCallRecovery = totalCalls * daysPerMonth * 0.30 * capabilityMultiplier; // Recover 30% of previously missed calls
    const patientSatisfactionImprovement = Math.min(40 * capabilityMultiplier, 60); // 40% improvement in patient satisfaction scores, capped at 60%

    return {
      // Basic metrics
      totalDailyCalls: totalCalls,
      totalMonthlyCalls: totalCalls * daysPerMonth,
      monthlySavingsHours: monthlySavingsHours.toFixed(1),
      monthlySavingsDollars: monthlySavingsDollars.toFixed(0),
      annualSavingsDollars: annualSavingsDollars.toFixed(0),

      // Revenue Domain
      newAppointmentsPerMonth: Math.round(newAppointmentsPerMonth),
      monthlyRevenueIncrease: Math.round(monthlyRevenueIncrease),
      annualRevenueIncrease: Math.round(annualRevenueIncrease),

      // Margins/Cost Savings Domain
      staffHoursSaved: monthlySavingsHours.toFixed(1),
      monthlyLaborCostSavings: monthlySavingsDollars.toFixed(0),
      annualLaborCostSavings: annualSavingsDollars.toFixed(0),
      fteReduction: (monthlySavingsHours / 160).toFixed(1), // Hours saved / standard monthly hours

      // Patient Access Domain
      waitTimeReduction: avgWaitTimeReduction.toFixed(1),
      callAnswerRate: (callsAnsweredRate * 100).toFixed(0),
      additionalPatientsServed: Math.round(missedCallRecovery),
      patientSatisfactionGain: patientSatisfactionImprovement.toFixed(1),

      // Ankr Cost (Mira & Frank)
      miraFTEs,
      frankFTEs,
      totalFTEs,
      totalWeeklyCost: (totalFTEs * 40 * 10.5).toFixed(0),
      totalBiWeeklyCost: (totalFTEs * 40 * 2 * 10.5).toFixed(0),
      totalMonthlyCost: totalMonthlyCost.toFixed(0),
      totalAnnualCost: totalAnnualCost.toFixed(0),

      // Net Impact (after Ankr costs)
      netMonthlySavings: (monthlySavingsDollars + monthlyRevenueIncrease - totalMonthlyCost).toFixed(0),
      netAnnualSavings: (annualSavingsDollars + annualRevenueIncrease - totalAnnualCost).toFixed(0),

      // ROI Ratio
      roiRatio: roiRatio.toFixed(1)
    };
  }, [selectedCapabilities, callVolumes, detailedCapabilities, manualMiraFTEs, manualFrankFTEs]);

  // Update Redux FTE values when calculations change
  useEffect(() => {
    updateMiraFTE(calculations.miraFTEs);
    updateFrankFTE(calculations.frankFTEs);
  }, [calculations.miraFTEs, calculations.frankFTEs, updateMiraFTE, updateFrankFTE]);

  // Initial sync between capabilities and tasks
  useEffect(() => {
    syncTasksFromCapabilities(selectedCapabilities);
  }, []); // Only run once on mount

  // Create session on page load 
  useEffect(() => {
    if (!isAuthenticated && !sessionCreationRef.current) {
      sessionCreationRef.current = true;
      dispatch(createSession());
      captureEvent(POSTHOG_EVENTS.SIGNUP_STARTED, { source: 'signup_page' });
    }
  }, [dispatch, isAuthenticated, captureEvent]);

  useEffect(() => {
    if (sessionError && sessionCreationRef.current) {
      sessionCreationRef.current = false;
    }
  }, [sessionError]);

  useEffect(() => {
    if (sessionId && !sessionData && !isFetchingSession) {
      dispatch(fetchSessionDetails(sessionId));
      identifyUser(sessionId, {
        session_id: sessionId,
        signup_source: 'onboarding_flow',
        created_at: new Date().toISOString(),
      });
    }
  }, [dispatch, sessionId, sessionData, isFetchingSession, identifyUser]);

  useEffect(() => {
    if (sessionId && (organizationName || selectedOrgId)) {
      identifyUser(sessionId, {
        organization_name: organizationName || undefined,
        organization_id: selectedOrgId || undefined,
        mira_fte: miraFTE || undefined,
        frank_fte: frankFTE || undefined,
      });
    }
  }, [sessionId, organizationName, selectedOrgId, miraFTE, frankFTE, identifyUser]);

  const searchOrganizations = async (searchQuery) => {
    if (searchQuery.length < 3) {
      updateOrgSearchResults([]);
      updateShowOrgSuggestions(false);
      return;
    }

    updateIsSearchingOrgs(true);
    try {
      const response = await api.get('/anthem/organization/search/', {
        params: { name: searchQuery }
      });

      if (response.data && response.data.result) {
        updateOrgSearchResults(response.data.result);
        updateShowOrgSuggestions(true);
      } else {
        updateOrgSearchResults([]);
        updateShowOrgSuggestions(false);
      }
    } catch (error) {
      console.error('Error searching organizations:', error);
      updateOrgSearchResults([]);
      updateShowOrgSuggestions(false);
    } finally {
      updateIsSearchingOrgs(false);
    }
  };

  const handleOrganizationChange = (e) => {
    const value = e.target.value;
    updateOrganizationName(value);
    updateSelectedOrgId(null);

    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    searchDebounceTimer.current = setTimeout(() => {
      searchOrganizations(value);
    }, 500);
  };

  const handleOrgSelect = (org) => {
    updateOrganizationName(org.name);
    updateSelectedOrgId(org.id);
    updateShowOrgSuggestions(false);
    updateOrgSearchResults([]);
  };

  const syncTasksFromCapabilities = (capabilities) => {
    // Update Mira tasks based on capabilities
    setMiraTasks(prev => ({
      ...prev,
      medicalIssues: capabilities.medicalIssues || false,
      medicalRecords: capabilities.medicalRecords || false,
      clinicalDocumentation: capabilities.medicalIssues || capabilities.medicalRecords || false
    }));

    // Update Frank tasks based on capabilities
    setFrankTasks(prev => ({
      ...prev,
      appointments: capabilities.appointments || false,
      medications: capabilities.medications || false,
      billingInsurance: capabilities.billingInsurance || false,
      vendorCalls: capabilities.vendorCalls || false
    }));
  };

  const syncCapabilitiesFromMiraTasks = (tasks) => {
    setSelectedCapabilities(prev => ({
      ...prev,
      medicalIssues: tasks.medicalIssues || false,
      medicalRecords: tasks.medicalRecords || false
    }));
  };

  const syncCapabilitiesFromFrankTasks = (tasks) => {
    setSelectedCapabilities(prev => ({
      ...prev,
      appointments: tasks.appointments || false,
      medications: tasks.medications || false,
      billingInsurance: tasks.billingInsurance || false,
      vendorCalls: tasks.vendorCalls || false
    }));
  };

  const handleCapabilityToggle = (capabilityId) => {
    const newSelectedCapabilities = {
      ...selectedCapabilities,
      [capabilityId]: !selectedCapabilities[capabilityId]
    };
    setSelectedCapabilities(newSelectedCapabilities);
    
    // Sync with AI Assistant tasks
    syncTasksFromCapabilities(newSelectedCapabilities);
  };

  const handleMiraTaskToggle = (taskId) => {
    const newMiraTasks = {
      ...miraTasks,
      [taskId]: !miraTasks[taskId]
    };
    setMiraTasks(newMiraTasks);
    
    // Update corresponding capabilities
    syncCapabilitiesFromMiraTasks(newMiraTasks);
  };

  const handleFrankTaskToggle = (taskId) => {
    const newFrankTasks = {
      ...frankTasks,
      [taskId]: !frankTasks[taskId]
    };
    setFrankTasks(newFrankTasks);
    
    // Update corresponding capabilities
    syncCapabilitiesFromFrankTasks(newFrankTasks);
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedCapabilities).every(v => v);
    const newState = {};
    Object.keys(selectedCapabilities).forEach(key => {
      newState[key] = !allSelected;
    });
    setSelectedCapabilities(newState);
    syncTasksFromCapabilities(newState);
  };

  const handleMiraFTEChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setManualMiraFTEs(numValue);
    }
  };

  const handleFrankFTEChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setManualFrankFTEs(numValue);
    }
  };

  const handleNext = async () => {
    if (!organizationName.trim()) {
      alert('Please enter your organization name');
      return;
    }
    
    const miraFTEValue = parseFloat(calculations.miraFTEs) || 0;
    const frankFTEValue = parseFloat(calculations.frankFTEs) || 0;
    
    if (miraFTEValue === 0 && frankFTEValue === 0) {
      alert('Please select at least one task for Mira or Frank');
      return;
    }
    
    // Sync selected capabilities into Redux cart for the Payment page
    updateCartCapabilities({ ...selectedCapabilities });
    
    // Start loading
    setIsProcessing(true);
    
    if (selectedOrgId) {
      setOrganizationIdState(selectedOrgId);
      
      try {
        setIsLoadingProviders(true);
        const providersResponse = await api.get(`/anthem/organization/${selectedOrgId}/providers/`);
        console.log('Providers fetched successfully:', providersResponse.data);
        setIsLoadingProviders(false);
      } catch (error) {
        console.error('Error fetching providers:', error);
        setIsLoadingProviders(false);
      }

      if (sessionId) {
        try {
          await api.patch(`/onboarding/sessions/${sessionId}/`, {
            healthcare_details: {
              organization_name: organizationName,
              anthem_id: selectedOrgId,
              mira_fte_count: miraFTEValue,
              frank_fte_count: frankFTEValue,
              selected_capabilities: selectedCapabilities,
              mira_tasks: miraTasks,
              frank_tasks: frankTasks
            }
          });
          console.log('Session updated with healthcare details');
        } catch (error) {
          console.error('Error updating session:', error);
        }
      }
    }
    
    // Stop loading
    setIsProcessing(false);
    
    // Check if user is authenticated before showing contract
    console.log('Authentication check:', { isAuthenticated, sessionId, sessionData });
    if (!isAuthenticated) {
      // User needs to login first
      console.log('User not authenticated, showing login modal');
      setPendingContractShow(true);
      setShowLoginModal(true);
    } else {
      // User is authenticated, show contract directly
      console.log('User authenticated, showing contract form');
      setShowContractForm(true);
    }
  };

  const handleContractSubmission = (submission) => {
    console.log('Contract signed successfully!', submission);
    setContractSigned(true);
    
    // Capture analytics event
    captureEvent(POSTHOG_EVENTS.CONTRACT_SIGNED, {
      sessionId,
      submissionId: submission.id,
      organizationName,
    });
    
    // Navigate to next step after a brief delay
    setTimeout(() => {
      navigate('/onboarding/payment');
    }, 2000);
  };

  const handleContractError = (error) => {
    console.error('Contract signing error:', error);
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingContractShow(false);
  };

  const handleLoginSuccess = () => {
    console.log('Login successful, showing contract form');
    setShowLoginModal(false);
    setPendingContractShow(false);
    setShowContractForm(true);
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Full Page Loading Overlay */}
      {(isProcessing || isLoadingProviders) && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(0,0,0,0.6)', zIndex: 10000, backdropFilter: 'blur(4px)' }}>
          <div className="bg-white p-4 px-5 rounded-4 shadow-lg d-flex flex-column align-items-center gap-3">
            <div className="spinner-border text-primary" style={{ width: '3.5rem', height: '3.5rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mb-0 fw-semibold text-dark text-center">
              {isLoadingProviders ? 'Loading provider information...' : 
               'Processing your request...'}
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow-1 p-4 mx-auto w-100" style={{ maxWidth: 1200 }}>
        {/* Hero Section */}
        <section className="text-center mb-4">
          <h1 className="display-5 fw-bold text-dark mb-2">Meet Your AI Team</h1>
          <p className="fs-6 text-secondary mx-auto" style={{ maxWidth: 600, lineHeight: 1.6 }}>
            Choose the capabilities you&apos;d like Mira and Frank to handle for your practice.
          </p>
        </section>

        {/* AI Assistants Section - Meet Your Team */}
        <section className="mb-4">
          <div className="row row-cols-1 row-cols-md-2 g-4">
            {/* Mira Card */}
            <div className="col">
              <div className="card border-0 shadow-sm overflow-hidden rounded-4">
                <div className="position-relative overflow-hidden" style={{ height: 220, background: '#fafafd' }}>
                  <img 
                    src="dist/assets/images/mira_ankr.jpg"
                    alt="Mira" 
                    className="w-100 h-100"
                    style={{ objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200x250?text=Mira';
                    }}
                  />
                  <span className="badge bg-success position-absolute fw-bold" style={{ top: '1rem', left: '1rem', fontSize: '0.65rem', letterSpacing: '0.5px' }}>CLINICAL EXPERT</span>
                  <div className="position-absolute bottom-0 start-0 end-0 p-3 text-white" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                    <h3 className="h5 fw-bold text-white mb-1">Mira</h3>
                    <p className="small text-white-50 mb-0">Specializing in clinical triage, medical history, and record management.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Frank Card */}
            <div className="col">
              <div className="card border-0 shadow-sm overflow-hidden rounded-4">
                <div className="position-relative overflow-hidden" style={{ height: 220, background: '#fafafd' }}>
                  <img 
                    src="/dist/assets/images/frank_ankr.jpg" 
                    alt="Frank"
                    className="w-100 h-100"
                    style={{ objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200x250?text=Frank';
                    }}
                  />
                  <span className="badge bg-warning text-white position-absolute fw-bold" style={{ top: '1rem', left: '1rem', fontSize: '0.65rem', letterSpacing: '0.5px' }}>ADMIN OPERATIONS</span>
                  <div className="position-absolute bottom-0 start-0 end-0 p-3 text-white" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}>
                    <h3 className="h5 fw-bold text-white mb-1">Frank</h3>
                    <p className="small text-white-50 mb-0">Master of scheduling, billing, and pharmacy logistics.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="mb-4">
          <div className="card border-0 shadow-sm p-4 position-relative bg-light rounded-4">
            <div className="d-flex flex-column gap-2 mb-4">
              <h2 className="h4 fw-bold mb-0">What should Mira and Frank handle?</h2>
              <p className="small text-secondary mb-0">Toggle the capabilities you&apos;d like your AI assistants to manage.</p>
              <button className="btn text-white position-absolute d-inline-flex align-items-center gap-2 fw-semibold" style={{ top: '1.5rem', right: '1.5rem', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: 10, padding: '0.875rem 2rem', fontSize: '0.875rem' }} onClick={handleSelectAll}>
                {Object.values(selectedCapabilities).every(v => v) ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Capabilities Grid */}
            <div className="row row-cols-1 row-cols-md-2 g-3 mb-4">
              {capabilities.map(capability => (
                <div className="col" key={capability.id}>
                  <div 
                    className={`card p-3 d-flex flex-row justify-content-between align-items-center ${selectedCapabilities[capability.id] ? 'border-success' : 'border'}`}
                    style={{ borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s ease', backgroundColor: selectedCapabilities[capability.id] ? '#f0fdf4' : '#ffffff' }}
                    onClick={() => handleCapabilityToggle(capability.id)}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="d-flex align-items-center justify-content-center text-white rounded-3 fs-5" style={{ width: 40, height: 40, minWidth: 40, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>{capability.icon}</span>
                      <div className="d-flex flex-column">
                        <h3 className="small fw-semibold text-dark mb-0">{capability.title}</h3>
                        <p className="small text-secondary mb-0" style={{ fontSize: '0.75rem' }}>{capability.description}</p>
                      </div>
                    </div>
                    <div className="form-check form-switch" onClick={(e) => e.stopPropagation()}>
                      <input 
                        className="form-check-input"
                        type="checkbox" 
                        id={`capability-${capability.id}`}
                        checked={selectedCapabilities[capability.id]}
                        onChange={() => handleCapabilityToggle(capability.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Organization Section */}
        <section className="text-center py-4 mx-auto" style={{ maxWidth: 600 }}>
          <h2 className="h4 fw-bold text-dark mb-2">Name of Your Organization?</h2>
          <p className="text-secondary mb-4">Provide your practice name to finalize assistant profiles.</p>
          
          <div className="position-relative mx-auto mb-4" style={{ maxWidth: 400 }}>
            <input
              type="text"
              className="form-control text-center rounded-3 py-2"
              placeholder="e.g. Riverside Healthcare Center"
              value={organizationName}
              onChange={handleOrganizationChange}
              onFocus={() => organizationName.length >= 3 && orgSearchResults.length > 0 && updateShowOrgSuggestions(true)}
            />
            {isSearchingOrgs && (
              <div className="position-absolute end-0 top-50 translate-middle-y pe-3 small text-primary">Searching...</div>
            )}
            {showOrgSuggestions && orgSearchResults.length > 0 && (
              <div className="list-group position-absolute w-100 mt-1 shadow-lg rounded-3 overflow-auto" style={{ maxHeight: 250, zIndex: 100 }}>
                {orgSearchResults.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    className="list-group-item list-group-item-action py-2 px-3"
                    onClick={() => handleOrgSelect(org)}
                  >
                    <span className="fw-medium">{org.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="btn fw-semibold text-white d-inline-flex align-items-center gap-2" style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', border: 'none', borderRadius: 10, padding: '0.6rem 1.5rem', fontSize: '0.9rem', boxShadow: '0 3px 8px rgba(124,58,237,0.25)' }} onClick={handleNext}>
            Continue to Onboarding
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ms-1">
              <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-3 px-4" style={{ backgroundColor: '#1e293b' }}>
        <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          <div className="fw-semibold text-white">Ankr Healthcare AI</div>
          <div className="d-flex gap-4">
            <a href="#" className="text-secondary text-decoration-none small">Support</a>
            <a href="#" className="text-secondary text-decoration-none small">Security</a>
            <a href="#" className="text-secondary text-decoration-none small">Privacy</a>
          </div>
          <div className="small text-secondary">© 2024 ANKR. COMPLIANT AI FOR MEDICINE.</div>
        </div>
      </footer>

      {/* Contract Signing Modal */}
      {showContractForm && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3 overflow-auto" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }} onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowContractForm(false);
          }
        }}>
          <div className="bg-white rounded-4 shadow-lg w-100 d-flex flex-column overflow-hidden" style={{ maxWidth: 1200, maxHeight: '90vh' }}>
            <div className="px-4 py-3 border-bottom bg-light flex-shrink-0" style={{ borderRadius: '1rem 1rem 0 0' }}>
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="h4 fw-bold text-dark mb-0">Master Service Agreement</h2>
                <button 
                  onClick={() => setShowContractForm(false)}
                  className="btn-close"
                  aria-label="Close modal"
                />
              </div>
              {contractSigned ? (
                <div className="alert alert-success fw-semibold text-center mt-2 mb-0">
                  ✓ Contract signed successfully! Redirecting...
                </div>
              ) : (
                <p className="text-secondary small mt-2 mb-0">Please review and sign the contract to complete your onboarding.</p>
              )}
            </div>
            
            <div className="flex-grow-1 overflow-auto">
              {!contractSigned && (
                <JotformEmbed
                  formId="260466314214046"
                  sessionId={sessionId || `session-${Date.now()}`}
                  contractType="msa"
                  signerName={organizationName}
                  signerEmail=""
                  organizationName={organizationName}
                  contractAttemptId={`attempt-${sessionId}`}
                  height={700}
                  enablePolling={true}
                  pollingInterval={5000}
                  onSubmissionReceived={handleContractSubmission}
                  onError={handleContractError}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default SignupPage;
